import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Clock, CurrencyDollar, Receipt, Storefront, TrendUp } from "@phosphor-icons/react";
import moment from "moment";
import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic-light-dark.css";
import PageFrame from "../../common/PageFrame";
import Loader from "../../common/Loader";
import { eventActions } from "../../redux/actions";

const firstValue = (sources, keys) => {
    for (const source of sources) {
        for (const key of keys) {
            if (source?.[key] !== undefined && source?.[key] !== null) return source[key];
        }
    }
    return null;
};

const numberValue = (value) => {
    const parsed = Number(String(value ?? "").replace(/[^0-9.-]+/g, ""));
    return Number.isFinite(parsed) ? parsed : null;
};

const formatMoney = (value, currency = "CAD") => {
    const amount = numberValue(value);
    if (amount === null) return "—";
    return new Intl.NumberFormat("en-CA", { style: "currency", currency, maximumFractionDigits: 2 }).format(amount);
};

const normalizeVendor = (vendor) => {
    const store = vendor?.store || vendor?.storefront || vendor?.stores?.[0] || {};
    const metrics = vendor?.metrics || store?.metrics || vendor?.analytics || store?.analytics || {};
    const sources = [metrics, store, vendor];

    return {
        id: vendor?.id || store?.id || vendor?.email,
        name: firstValue(sources, ["name", "business_name", "store_name"]) || "Unnamed vendor",
        email: vendor?.email || store?.email || "",
        logo: firstValue(sources, ["logo_url", "banner_url", "image_url", "photo_url"]),
        sales: firstValue(sources, ["total_sales", "totalSales", "total_earnings", "revenue", "gross_sales"]),
        orders: firstValue(sources, ["total_orders", "totalOrders", "order_count", "orders_count"]),
        busyHour: firstValue(sources, ["busy_hour", "busiest_hour", "peak_hour", "peak_sales_hour"]),
        topItem: firstValue(sources, ["top_item", "most_ordered_item", "popular_item", "best_seller"]),
        currency: firstValue(sources, ["currency", "currency_code"]) || "CAD",
        hourly: firstValue(sources, ["hourly_sales", "sales_by_hour", "orders_by_hour", "busy_hours"]),
    };
};

const normalizeHourlyData = (vendors) => {
    const totals = new Map();

    vendors.forEach((vendor) => {
        if (!Array.isArray(vendor.hourly)) return;
        vendor.hourly.forEach((entry) => {
            const label = entry?.hour || entry?.time || entry?.label;
            const value = numberValue(entry?.sales ?? entry?.orders ?? entry?.quantity ?? entry?.total);
            if (label && value !== null) totals.set(label, (totals.get(label) || 0) + value);
        });
    });

    return Array.from(totals, ([label, value]) => ({ label, value }));
};

const EventInsights = () => {
    const { eventId } = useParams();
    const dispatch = useDispatch();
    const [page, setPage] = useState(1);
    const [vendors, setVendors] = useState([]);
    const [pageCount, setPageCount] = useState(1);
    const [vendorCount, setVendorCount] = useState(0);

    const {
        isFetchingSingleEvent,
        isFetchedSingleEventSuccess,
        isFetchingEventVendors,
        isFetchedEventVendorsSuccess,
    } = useSelector((store) => store.eventManager);

    useEffect(() => {
        if (eventId) dispatch(eventActions.getSingleEvent(eventId));
    }, [dispatch, eventId]);

    useEffect(() => {
        if (eventId) dispatch(eventActions.getEventVendors(eventId, page));
    }, [dispatch, eventId, page]);

    useEffect(() => {
        if (!isFetchedEventVendorsSuccess) return;
        setVendors((isFetchedEventVendorsSuccess?.data || []).map(normalizeVendor));
        setPageCount(isFetchedEventVendorsSuccess?.meta?.last_page || 1);
        setVendorCount(isFetchedEventVendorsSuccess?.meta?.total || isFetchedEventVendorsSuccess?.data?.length || 0);
    }, [isFetchedEventVendorsSuccess]);

    const reportedSales = useMemo(() => vendors.reduce((total, vendor) => total + (numberValue(vendor.sales) || 0), 0), [vendors]);
    const reportedOrders = useMemo(() => vendors.reduce((total, vendor) => total + (numberValue(vendor.orders) || 0), 0), [vendors]);
    const vendorsWithSales = useMemo(() => vendors.filter((vendor) => numberValue(vendor.sales) !== null).length, [vendors]);
    const hourlyData = useMemo(() => normalizeHourlyData(vendors), [vendors]);
    const maxHourlyValue = Math.max(...hourlyData.map((item) => item.value), 1);
    const busiestPeriod = hourlyData.length ? hourlyData.reduce((highest, item) => item.value > highest.value ? item : highest) : null;
    const event = isFetchedSingleEventSuccess || {};

    return (
        <PageFrame
            isLoading={isFetchingSingleEvent}
            frameLinks={[
                { text: "Event Details", url: `/dashboard/event/${eventId}`, isActive: false },
                { text: "Event insights", url: `/dashboard/event/${eventId}/insights`, isActive: true },
            ]}
            childrenContainerClass="insights-main"
        >
            <div className="event-insights-page">
                <header className="insights-header">
                    <div>
                        <p className="noq-page-eyebrow">EVENT PERFORMANCE</p>
                        <h1>Event insights</h1>
                        <p>{event?.name || "Event"}{event?.start_date ? ` · ${moment(event.start_date).format("MMM D, YYYY")}` : ""}</p>
                    </div>
                    <span className="insights-data-note">Live event vendor data</span>
                </header>

                <section className="insights-stat-grid" aria-label="Event summary">
                    <article className="insights-stat"><Storefront size={20} /><span>Vendors</span><strong>{vendorCount}</strong><small>Participating in this event</small></article>
                    <article className="insights-stat"><CurrencyDollar size={20} /><span>Reported sales</span><strong>{vendorsWithSales ? formatMoney(reportedSales) : "—"}</strong><small>{vendorsWithSales ? `${vendorsWithSales} vendor${vendorsWithSales === 1 ? "" : "s"} reporting` : "Awaiting sales data"}</small></article>
                    <article className="insights-stat"><Receipt size={20} /><span>Reported orders</span><strong>{vendors.some((vendor) => numberValue(vendor.orders) !== null) ? reportedOrders.toLocaleString("en-CA") : "—"}</strong><small>Completed vendor orders</small></article>
                    <article className="insights-stat"><Clock size={20} /><span>Busiest period</span><strong>{busiestPeriod?.label || "—"}</strong><small>{busiestPeriod ? `${busiestPeriod.value.toLocaleString("en-CA")} recorded` : "Awaiting hourly data"}</small></article>
                </section>

                <div className="insights-content-grid">
                    <section className="insights-panel insights-vendors-panel">
                        <div className="insights-panel-heading">
                            <div><h2>Vendor performance</h2><p>Sales, order volume, busy periods, and leading items.</p></div>
                            <span>{vendors.length} shown</span>
                        </div>

                        {isFetchingEventVendors ? (
                            <div className="insights-loading"><Loader color="#CEF24E" /><span>Loading vendors</span></div>
                        ) : vendors.length ? (
                            <div className="insights-table-wrap">
                                <table className="insights-table">
                                    <thead><tr><th>Vendor</th><th>Sales</th><th>Orders</th><th>Busy hour</th><th>Top item</th></tr></thead>
                                    <tbody>
                                        {vendors.map((vendor) => (
                                            <tr key={vendor.id}>
                                                <td><div className="vendor-identity">{vendor.logo ? <img src={vendor.logo} alt="" /> : <span><Storefront size={19} /></span>}<div><strong>{vendor.name}</strong>{vendor.email && <small>{vendor.email}</small>}</div></div></td>
                                                <td className="insights-money">{formatMoney(vendor.sales, vendor.currency)}</td>
                                                <td>{numberValue(vendor.orders)?.toLocaleString("en-CA") || "—"}</td>
                                                <td>{vendor.busyHour || "—"}</td>
                                                <td>{vendor.topItem || "—"}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="insights-empty"><Storefront size={34} /><h3>No vendors to report yet</h3><p>Vendor performance appears here after vendors join the event.</p></div>
                        )}

                        {pageCount > 1 && <div className="insights-pagination"><ResponsivePagination current={page} total={pageCount} maxWidth={260} onPageChange={setPage} /></div>}
                    </section>

                    <section className="insights-panel insights-hours-panel">
                        <div className="insights-panel-heading"><div><h2>Busy hours</h2><p>Combined vendor activity throughout the event.</p></div><TrendUp size={22} /></div>
                        {hourlyData.length ? (
                            <div className="busy-hours-chart">
                                {hourlyData.map((item) => <div className="busy-hour-row" key={item.label}><span>{item.label}</span><div><i style={{ width: `${Math.max((item.value / maxHourlyValue) * 100, 3)}%` }} /></div><strong>{item.value.toLocaleString("en-CA")}</strong></div>)}
                            </div>
                        ) : (
                            <div className="insights-empty insights-empty--compact"><Clock size={31} /><h3>No hourly data yet</h3><p>Busy periods will appear when vendor order metrics are available.</p></div>
                        )}
                    </section>
                </div>
            </div>
        </PageFrame>
    );
};

export default EventInsights;
